import type { Messages } from "./messages.ts";

export const fr: Messages = {
  app: {
    name: "Boomap",
    tagline: "Les maisons à bonbons du quartier, le soir d'Halloween.",
    description:
      "Boomap : la carte collaborative des maisons qui distribuent des bonbons le soir d'Halloween.",
    mapLabel: "Carte des maisons distribuant des bonbons",
  },

  treats: {
    available: {
      label: "Des bonbons",
      hint: "Cette maison distribue des bonbons ce soir.",
    },
    exhausted: {
      label: "Plus de bonbons",
      hint: "Le stock est épuisé, inutile de sonner.",
    },
    none: {
      label: "Pas de bonbons",
      hint: "Rien à récolter ici.",
    },
  },

  legend: {
    label: "Légende de la carte",
    title: "Légende",
    open: "Afficher la légende et les mentions légales",
  },

  add: {
    label: "Ajouter une maison",
  },

  placement: {
    regionLabel: "Placement d'une pastille",
    title: "Placez le repère sur la maison.",
    hint: "Faites glisser la carte — le repère reste au centre.",
    cancel: "Annuler",
    confirm: "Continuer",
  },

  declare: {
    title: "Cette maison…",
    treatsLegend: "Bonbons ce soir",
    noteLabel: "Précision facultative",
    notePlaceholder: "Sonnez deux fois, portail ouvert jusqu'à 21h…",
    location: ({ latitude, longitude }) =>
      `${latitude}, ${longitude} — la pastille disparaîtra automatiquement au lendemain d'Halloween.`,
    cancelLabel: "Annuler",
    cancel: "Annuler",
    submit: "Poser la pastille",
    submitting: "Enregistrement…",
  },

  spot: {
    closeLabel: "Fermer",
    declaredAt: (time) => `Déclarée à ${time}`,
    updated: (relative) => `Mise à jour ${relative}`,
    justUpdated: "Mise à jour à l'instant",
    // Le français garde le singulier à zéro comme à un.
    reports: (count) => `${count.toString()} signalement${count > 1 ? "s" : ""}`,
    report: "Signaler une erreur",
    reporting: "Envoi…",
    close: "Fermer",
    retract: "Supprimer ma pastille",
    retracting: "Suppression…",
    takeDownLink: "C'est mon logement, le retirer de la carte",
    takeDown: {
      title: "Retirer ce logement ?",
      effect:
        "La pastille disparaîtra immédiatement, et cet emplacement ne pourra pas être redéclaré cette saison.",
      attestation: "En confirmant, vous déclarez être l'occupant de ce logement.",
      cancel: "Annuler",
      confirm: "Retirer",
      submitting: "Retrait…",
    },
  },

  search: {
    label: "Rechercher un lieu",
    placeholder: "Ville, rue, adresse…",
    clear: "Effacer la recherche",
    empty: "Aucun lieu ne correspond.",
    tooShort: "Encore un caractère…",
  },

  banner: {
    zoomedOut: "Zoomez ou cherchez une ville pour voir les maisons.",
    truncated: "Trop de maisons dans cette zone : zoomez pour toutes les voir.",
  },

  toast: {
    added: "Pastille ajoutée. Merci !",
    alreadyThere: "Cette maison est déjà sur la carte.",
    removed: "Merci, la pastille a été retirée de la carte.",
    reported: "Signalement enregistré.",
    alreadyReported: "Vous avez déjà signalé cette maison.",
    takenDown: "Le logement a été retiré de la carte.",
    retracted: "Votre pastille a été supprimée.",
  },

  locate: {
    label: "Me localiser",
    unsupported: "Ce navigateur ne sait pas se géolocaliser.",
    denied: "Géolocalisation refusée ou indisponible.",
  },

  basemap: {
    night: "nuit",
    day: "jour",
    switchTo: (name) => `Passer au fond de carte ${name}`,
  },

  localeSwitch: (name) => `Afficher le site en ${name}`,

  legal: {
    link: "Vie privée",
    title: "Vie privée & mentions légales",
    close: "Fermer",
    sections: [
      {
        heading: "Un site communautaire",
        body:
          "Toutes les pastilles sont posées par des visiteurs. Rien n'est vérifié, ni par " +
          "nous ni par personne : une maison indiquée peut ne pas distribuer de bonbons, et " +
          "une maison qui en distribue peut être absente de la carte. Traitez cette carte " +
          "comme une indication donnée par des voisins, pas comme une information officielle.",
      },
      {
        heading: "Les enfants restent sous la responsabilité de leurs parents",
        body:
          "Cette carte est une aide, pas un accompagnement. Les parents ou les responsables " +
          "légaux demeurent seuls responsables de la surveillance de leurs enfants mineurs, " +
          "du choix des maisons auxquelles ils sonnent, du trajet emprunté et de ce qui est " +
          "récolté. Nous ne connaissons ni les habitants, ni les lieux, ni les friandises.",
      },
      {
        heading: "Aucun cookie, aucune mesure d'audience",
        body:
          "Ce site ne dépose aucun cookie et n'utilise aucun outil de mesure d'audience " +
          "ou de publicité. C'est pourquoi aucun bandeau de consentement ne vous est " +
          "présenté : il n'y aurait rien à consentir.",
      },
      {
        heading: "Ce qui est enregistré dans votre navigateur",
        body:
          "Deux préférences d'affichage seulement, écrites quand vous cliquez sur le " +
          "bouton correspondant : la langue et le fond de carte jour/nuit. Elles restent " +
          "dans votre navigateur et ne sont jamais envoyées au serveur. Elles relèvent " +
          "des traceurs exemptés de consentement, la CNIL citant explicitement la " +
          "personnalisation de l'interface.",
      },
      {
        heading: "Ce qui est enregistré sur le serveur",
        body:
          "Pour chaque pastille : un point sur la carte, l'état des bonbons, la " +
          "décoration, votre éventuelle note, et des dates. Votre adresse IP est lue puis " +
          "transformée en une empreinte irréversible, salée par un secret et par la date " +
          "du jour ; elle sert uniquement à limiter les abus et se périme chaque nuit. " +
          "L'adresse IP n'est jamais conservée en clair, ni en base, ni dans les journaux.",
      },
      {
        heading: "Votre note est publique",
        body:
          "Le texte libre que vous ajoutez à une pastille est visible de tous. N'y écrivez " +
          "ni nom, ni numéro de téléphone, ni information concernant quelqu'un d'autre.",
      },
      {
        heading: "Fonds de carte",
        body:
          "Les tuiles cartographiques sont chargées par votre navigateur depuis " +
          "tiles.versatiles.org, ce qui transmet votre adresse IP à ce service, comme pour " +
          "toute image chargée depuis un autre site. La recherche de lieux, elle, passe par " +
          "notre serveur : votre navigateur ne contacte jamais l'annuaire directement.",
      },
      {
        heading: "Géolocalisation",
        body:
          "Votre position n'est demandée que si vous appuyez sur le bouton de " +
          "localisation, et votre navigateur vous en demande l'autorisation. Elle sert à " +
          "centrer la carte, n'est ni enregistrée ni transmise à personne.",
      },
      {
        heading: "Durée de conservation",
        body:
          "Tout expire automatiquement le 1er novembre à 6 h. Aucune donnée n'est " +
          "conservée d'une année sur l'autre.",
      },
      {
        heading: "C'est votre logement ?",
        body:
          "Ouvrez la pastille concernée et utilisez « C'est mon logement, le retirer de " +
          "la carte ». Le retrait est immédiat et l'emplacement ne peut pas être redéclaré " +
          "pour la saison. Vous n'avez besoin de l'accord de personne.",
      },
    ],
    identity: {
      heading: "Mentions légales",
      publisher: "Site édité par un particulier à titre non professionnel.",
      contactLabel: "Contact",
    },
    updated: (date) => `Dernière mise à jour : ${date}`,
  },

  errors: {
    invalid_value: "Ces informations ne sont pas valides.",
    spot_not_found: "Cette pastille n'existe plus.",
    spot_already_declared_nearby: "Une pastille existe déjà à quelques mètres.",
    not_the_author:
      "Cette pastille a été déclarée depuis un autre appareil, ou hier — elle ne peut plus être retirée ainsi.",
    location_taken_down:
      "L'occupant de ce logement a demandé son retrait de la carte. Merci de respecter sa décision.",
    takedown_quota_exceeded: "Vous avez atteint votre limite de retraits. Réessayez demain.",
    contribution_quota_exceeded:
      "Vous avez atteint votre quota de déclarations. Réessayez plus tard.",
    place_search_unavailable: "La recherche de lieux est momentanément indisponible.",
    not_found: "Ressource introuvable.",
    internal_error: "Une erreur inattendue est survenue.",
    network: "Une erreur réseau est survenue. Réessayez dans un instant.",
    spotsUnavailable: "Impossible de charger les pastilles pour le moment.",
  },
};
